import { useAuth } from "@clerk/react";
import { setAuthTokenGetter } from "@workspace/api-client-react";
import { useLayoutEffect, useRef, useState } from "react";
import { usesRemoteApiOnLocalhost } from "@/lib/api-config";

/**
 * Local web dev against a remote API cannot rely on Clerk session
 * cookies cross-origin — attach the Clerk JWT like the mobile app does.
 */
export function useRemoteApiAuthToken(): { authReady: boolean } {
  const useRemote = usesRemoteApiOnLocalhost();
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const [authReady, setAuthReady] = useState(!useRemote);

  useLayoutEffect(() => {
    if (!useRemote) {
      setAuthReady(true);
      return;
    }

    if (!isLoaded) {
      setAuthReady(false);
      return;
    }

    if (!isSignedIn) {
      setAuthTokenGetter(null);
      setAuthReady(true);
      return;
    }

    setAuthTokenGetter(async () => {
      const token = await getTokenRef.current();
      return token ?? null;
    });
    setAuthReady(true);

    return () => {
      setAuthTokenGetter(null);
    };
  }, [useRemote, isLoaded, isSignedIn]);

  return { authReady };
}
